import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { Switch } from "@/components/ui/switch";
import {
  adminCreateListing,
  adminDeleteListing,
  adminListBoards,
  adminListListings,
  adminUpdateListing,
} from "@/lib/admin.functions";
import { enrichListingFromUrl } from "@/lib/enrich.functions";
import { LISTING_TYPES, labelForType } from "@/lib/behot";

import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm/listings")({
  head: () =>
    privatePageMetadata(
      "/nc/live/adm/listings",
      "Listings | Control room",
      "Internal administration.",
    ),
  component: AdminListings,
});

const STATUSES = ["all", "pending", "draft", "approved", "rejected", "suspended"] as const;
const SOCIAL_KEYS = [
  "instagram",
  "tiktok",
  "youtube",
  "x",
  "spotify",
  "soundcloud",
  "facebook",
  "linkedin",
] as const;

type Status = "pending" | "draft" | "approved" | "rejected" | "suspended";

function AdminListings() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<(typeof STATUSES)[number]>("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<string | null>(null);

  const list = useServerFn(adminListListings);
  const update = useServerFn(adminUpdateListing);
  const remove = useServerFn(adminDeleteListing);

  const { data: listings = [], isLoading } = useQuery({
    queryKey: ["adm-listings", status, search],
    queryFn: () => list({ data: { status, ...(search ? { search } : {}) } }),
  });

  const save = useMutation({
    mutationFn: (input: {
      listingId: string;
      moderationStatus?: Status;
      isPublic?: boolean;
      isPlatformAffiliated?: boolean;
      name?: string;
      headline?: string;
      websiteUrl?: string;
      categoryId?: string;
    }) => update({ data: input }),
    onSuccess: () => {
      toast.success("Listing updated.");
      void qc.invalidateQueries({ queryKey: ["adm-listings"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed."),
  });

  const destroy = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => {
      toast.success("Listing deleted.");
      void qc.invalidateQueries({ queryKey: ["adm-listings"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Delete failed."),
  });

  const boards = useServerFn(adminListBoards);
  const { data: boardData } = useQuery({
    queryKey: ["adm-boards-lite"],
    queryFn: () => boards({ data: undefined }),
  });
  const categories = boardData?.categories ?? [];

  const [showNew, setShowNew] = useState(false);
  const emptyForm = {
    name: "",
    slug: "",
    listingType: "creator" as string,
    headline: "",
    websiteUrl: "",
    shortDescription: "",
    longDescription: "",
    city: "",
    countryCode: "",
    logoUrl: "",
    coverUrl: "",
    brandColor: "",
    tags: "",
    socials: {} as Record<string, string>,
    categoryId: "",
    heat: 0,
    days: 30,
    moderationStatus: "approved" as Status,
    isPublic: true,
    isPlatformAffiliated: false,
  };
  const [form, setForm] = useState(emptyForm);

  const enrich = useServerFn(enrichListingFromUrl);
  const [fetching, setFetching] = useState(false);

  async function handleFetch() {
    const url = form.websiteUrl.trim();
    if (!url) {
      toast.error("Paste a link first (website, socials, store…).");
      return;
    }
    setFetching(true);
    try {
      const data = await enrich({ data: { url } });
      setForm((f) => ({
        ...f,
        websiteUrl: data.url || f.websiteUrl,
        name: f.name || data.name || "",
        headline: f.headline || data.headline || "",
        shortDescription: f.shortDescription || (data.description ?? "").slice(0, 280),
        longDescription: f.longDescription || data.description || "",
        logoUrl: data.logoUrl || f.logoUrl,
        coverUrl: data.coverUrl || f.coverUrl,
        brandColor: data.brandColor || f.brandColor,
        tags: f.tags || (data.tags ?? []).join(", "),
        socials: Object.keys(data.socials ?? {}).length ? data.socials : f.socials,
      }));
      toast.success("Details fetched — review and edit before creating.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not fetch that link.");
    } finally {
      setFetching(false);
    }
  }

  const createFn = useServerFn(adminCreateListing);
  const create = useMutation({
    mutationFn: () => {
      const cleanSocials = Object.fromEntries(
        Object.entries(form.socials).filter(([, v]) => String(v ?? "").trim().length > 0),
      );
      const tags = form.tags

        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
        .slice(0, 20);
      return createFn({
        data: {
          name: form.name.trim(),
          ...(form.slug.trim() ? { slug: form.slug.trim() } : {}),
          listingType: form.listingType,
          ...(form.headline.trim() ? { headline: form.headline.trim() } : {}),
          ...(form.websiteUrl.trim() ? { websiteUrl: form.websiteUrl.trim() } : {}),
          ...(form.shortDescription.trim()
            ? { shortDescription: form.shortDescription.trim() }
            : {}),
          ...(form.longDescription.trim() ? { longDescription: form.longDescription.trim() } : {}),
          ...(form.city.trim() ? { city: form.city.trim() } : {}),
          ...(form.countryCode.trim()
            ? { countryCode: form.countryCode.trim().toUpperCase().slice(0, 2) }
            : {}),
          ...(form.logoUrl.trim() ? { logoUrl: form.logoUrl.trim() } : {}),
          ...(form.coverUrl.trim() ? { coverUrl: form.coverUrl.trim() } : {}),
          ...(form.brandColor.trim() ? { brandColor: form.brandColor.trim() } : {}),
          ...(tags.length ? { tags } : {}),
          ...(Object.keys(cleanSocials).length ? { socials: cleanSocials } : {}),
          ...(form.categoryId ? { categoryId: form.categoryId } : {}),
          heat: Number(form.heat) || 0,
          days: Number(form.days) || 30,
          moderationStatus: form.moderationStatus,
          isPublic: form.isPublic,
          isPlatformAffiliated: form.isPlatformAffiliated,
        },
      });
    },
    onSuccess: (res) => {
      toast.success(`Listing created (/p/${res.slug}).`);
      setShowNew(false);
      setForm(emptyForm);
      void qc.invalidateQueries({ queryKey: ["adm-listings"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Create failed."),
  });


  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">Listings</h2>
        <Button size="sm" variant="hot" onClick={() => setShowNew((v) => !v)}>
          {showNew ? "Close" : "New listing"}
        </Button>
      </div>

      {showNew && (
        <div className="card-surface grid gap-3 p-4 md:grid-cols-2">
          <div className="space-y-1 md:col-span-2">
            <Label>Fetch from link (website, socials, store…)</Label>
            <div className="flex gap-2">
              <Input
                value={form.websiteUrl}
                placeholder="https://…"
                onChange={(e) => setForm({ ...form, websiteUrl: e.target.value })}
              />
              <Button type="button" variant="outline" onClick={handleFetch} disabled={fetching}>
                {fetching ? "Fetching…" : "Fetch"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Auto-fills name, headline, descriptions, logo, cover, colour, tags and socials. Every
              field stays editable — or skip fetching and fill it in manually.
            </p>
          </div>
          <div className="space-y-1">

            <Label>Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label>Slug (optional)</Label>
            <Input
              value={form.slug}
              placeholder="auto from name"
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label>Type</Label>
            <select
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={form.listingType}
              onChange={(e) => setForm({ ...form, listingType: e.target.value })}
            >
              {LISTING_TYPES.map((t) => (
                <option key={t} value={t}>
                  {labelForType(t)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label>Board category</Label>
            <select
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={form.categoryId}
              onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
            >
              <option value="">None</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label>Headline</Label>
            <Input
              value={form.headline}
              onChange={(e) => setForm({ ...form, headline: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label>City</Label>
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>Short description</Label>
            <Input
              value={form.shortDescription}
              onChange={(e) => setForm({ ...form, shortDescription: e.target.value })}
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>Long description</Label>
            <Textarea
              rows={5}
              value={form.longDescription}
              onChange={(e) => setForm({ ...form, longDescription: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label>Country code (ISO 2)</Label>
            <Input
              value={form.countryCode}
              placeholder="AE"
              maxLength={2}
              onChange={(e) => setForm({ ...form, countryCode: e.target.value.toUpperCase() })}
            />
          </div>
          <div className="space-y-1">
            <Label>Brand colour</Label>
            <Input
              value={form.brandColor}
              placeholder="#FF4D4D"
              onChange={(e) => setForm({ ...form, brandColor: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label>Logo URL</Label>
            <Input
              value={form.logoUrl}
              onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label>Cover URL</Label>
            <Input
              value={form.coverUrl}
              onChange={(e) => setForm({ ...form, coverUrl: e.target.value })}
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>Tags (comma separated)</Label>
            <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>Socials</Label>
            <div className="grid gap-2 sm:grid-cols-2">
              {SOCIAL_KEYS.map((k) => (
                <Input
                  key={k}
                  placeholder={k}
                  value={form.socials[k] ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, socials: { ...f.socials, [k]: e.target.value } }))
                  }
                />
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <Label>Granted heat (board position)</Label>
            <Input
              type="number"
              min={0}
              value={form.heat}
              onChange={(e) => setForm({ ...form, heat: Number(e.target.value) })}
            />
          </div>
          <div className="space-y-1">
            <Label>Boost days</Label>
            <Input
              type="number"
              min={1}
              value={form.days}
              onChange={(e) => setForm({ ...form, days: Number(e.target.value) })}
            />
          </div>
          <div className="space-y-1">
            <Label>Status</Label>
            <select
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={form.moderationStatus}
              onChange={(e) => setForm({ ...form, moderationStatus: e.target.value as Status })}
            >
              {(["approved", "pending", "draft", "rejected", "suspended"] as Status[]).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-6 pt-6">
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={form.isPublic}
                onCheckedChange={(v) => setForm({ ...form, isPublic: v })}
              />
              Public
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={form.isPlatformAffiliated}
                onCheckedChange={(v) => setForm({ ...form, isPlatformAffiliated: v })}
              />
              Platform listing
            </label>
          </div>
          <div className="md:col-span-2">
            <Button
              variant="hot"
              disabled={create.isPending || form.name.trim().length < 2}
              onClick={() => create.mutate()}
            >
              {create.isPending ? "Creating…" : "Create listing"}
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`rounded-full border px-3 py-1 text-xs font-medium capitalize ${
              status === s ? "bg-primary text-primary-foreground" : "hover:bg-muted"
            }`}
          >
            {s}
          </button>
        ))}
        <Input
          placeholder="Search listings"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="ml-auto max-w-xs"
        />
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      <div className="space-y-2">
        {listings.map((l) => (
          <div key={l.id} className="card-surface p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{l.name}</p>
                <p className="text-xs text-muted-foreground">
                  {labelForType(l.listing_type)} · {l.moderation_status} ·{" "}
                  {l.click_count} clicks · {new Date(l.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="hot"
                  onClick={() => save.mutate({ listingId: l.id, moderationStatus: "approved" })}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => save.mutate({ listingId: l.id, moderationStatus: "rejected" })}
                >
                  Reject
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => save.mutate({ listingId: l.id, moderationStatus: "suspended" })}
                >
                  Suspend
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <Link to="/p/$slug" params={{ slug: l.slug }}>
                    View
                  </Link>
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setOpen(open === l.id ? null : l.id)}>
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive"
                  disabled={destroy.isPending}
                  onClick={() => {
                    if (confirm(`Delete "${l.name}" permanently? This removes its boosts and board positions.`))
                      destroy.mutate(l.id);
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>

            {open === l.id && (
              <div className="mt-4 grid gap-3 border-t pt-4 md:grid-cols-2">
                <div className="space-y-1">
                  <Label>Name</Label>
                  <Input
                    defaultValue={l.name}
                    onBlur={(e) => save.mutate({ listingId: l.id, name: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Headline</Label>
                  <Input
                    defaultValue={l.headline ?? ""}
                    onBlur={(e) => save.mutate({ listingId: l.id, headline: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Board (category)</Label>
                  <select
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    defaultValue={
                      (l as { listing_categories?: { category_id: string }[] })
                        .listing_categories?.[0]?.category_id ?? ""
                    }
                    onChange={(e) => {
                      if (e.target.value)
                        save.mutate({ listingId: l.id, categoryId: e.target.value });
                    }}
                  >
                    <option value="">Select a board…</option>
                    {categories.map((c: { id: string; name: string }) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label>Website</Label>
                  <Input
                    defaultValue={l.website_url ?? ""}
                    onBlur={(e) => save.mutate({ listingId: l.id, websiteUrl: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-6 pt-6">
                  <label className="flex items-center gap-2 text-sm">
                    <Switch
                      checked={Boolean(l.is_public)}
                      onCheckedChange={(v) => save.mutate({ listingId: l.id, isPublic: v })}
                    />
                    Public
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Switch
                      checked={Boolean(l.is_platform_affiliated)}
                      onCheckedChange={(v) =>
                        save.mutate({ listingId: l.id, isPlatformAffiliated: v })
                      }
                    />
                    Platform listing
                  </label>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ml-auto text-destructive"
                    onClick={() => {
                      if (confirm(`Delete "${l.name}" permanently?`)) destroy.mutate(l.id);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
